import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text, Grid, SimpleGrid, chakra } from '@chakra-ui/react';
import { useInterval } from '@/hooks/useInterval';
import { api } from '@/lib/api';
import { useToast } from '@/components/ui/Toast';
import { Card } from '@/components/ui/Card';
import { Badge, SentimentBadge, RegimeBadge } from '@/components/ui/Badge';
import { PriceDisplay } from '@/components/ui/PriceDisplay';
import { Button } from '@/components/ui/Button';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { AddInstrumentDialog } from '@/components/AddInstrumentDialog';
import { CADENCE, WATCHLIST_LIMITS, isMarketHours } from '@market-buddy/shared';
import type { WatchlistItemWithData, WatchlistRecommendation, InsiderAlert } from '@market-buddy/shared';

interface BrokerAccount {
  cash: number;
  total: number;
  ppl: number;
  result: number;
}

interface AutoTradeConfig {
  enabled: boolean;
}

export function Watchlist() {
  const navigate = useNavigate();
  const [items, setItems] = useState<WatchlistItemWithData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const { toast } = useToast();

  // Account summary
  const [account, setAccount] = useState<BrokerAccount | null>(null);
  const [autoTrade, setAutoTrade] = useState<AutoTradeConfig | null>(null);

  // Watchlist meta (count/limit)
  const [watchlistMeta, setWatchlistMeta] = useState<{ count: number; limit: number } | null>(null);

  // Insider Alerts
  const [insiderAlerts, setInsiderAlerts] = useState<InsiderAlert[]>([]);
  const [insiderLoading, setInsiderLoading] = useState(false);
  // AI Recommendations
  const [recommendations, setRecommendations] = useState<WatchlistRecommendation[]>([]);
  const [recsLoading, setRecsLoading] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      api.get<BrokerAccount>('/api/broker/account'),
      api.get<AutoTradeConfig>('/api/broker/auto-trade'),
    ]).then(([accRes, atRes]) => {
      if (accRes.status === 'fulfilled') setAccount(accRes.value);
      if (atRes.status === 'fulfilled') setAutoTrade(atRes.value);
    });
  }, []);

  async function handleRemove(e: React.MouseEvent, item: WatchlistItemWithData) {
    e.stopPropagation();
    if (removingId) return;
    setRemovingId(item.id);
    try {
      await api.delete(`/api/watchlist/${item.id}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast(`${item.symbol} removed from watchlist`, 'info');
    } catch {
      toast('Failed to remove instrument', 'error');
    } finally {
      setRemovingId(null);
    }
  }

  const fetchWatchlist = useCallback(async () => {
    try {
      const data = await api.get<{ items: WatchlistItemWithData[]; meta?: { count: number; limit: number } }>('/api/watchlist');
      setItems(data.items);
      if (data.meta) setWatchlistMeta(data.meta);
      setError(null);
    } catch (err) {
      if (loading) setError('Unable to load watchlist');
      console.error('Watchlist fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const fetchRecommendations = useCallback(async () => {
    setRecsLoading(true);
    try {
      const data = await api.get<{
        recommendations: WatchlistRecommendation[];
        cachedAt: number | null;
      }>('/api/watchlist/recommendations');
      setRecommendations(data.recommendations);
    } catch (err) {
      console.error('Recommendations fetch error:', err);
    } finally {
      setRecsLoading(false);
    }
  }, []);

  const fetchInsiderAlerts = useCallback(async () => {
    setInsiderLoading(true);
    try {
      const data = await api.get<{ alerts: InsiderAlert[] }>('/api/insider/alerts');
      setInsiderAlerts(data.alerts ?? []);
    } catch (err) {
      console.error('Insider alerts fetch error:', err);
    } finally {
      setInsiderLoading(false);
    }
  }, []);

  const reviewCandidates = useMemo(() => {
    const now = Date.now();
    const DATA_MATURITY_MS = 30 * 60 * 1000; // 6 candle cycles for EMA convergence
    return items
      .map((item) => {
        // Skip instruments without mature data
        if (!item.lastAnalysed || item.lastAnalysed === 0) return null;
        if (now - item.lastAnalysed < DATA_MATURITY_MS) return null;

        const reasons: string[] = [];
        const isDead = item.regime === 'dead';
        const isRanging = item.regime === 'ranging';
        const isLowOpp = item.opportunityScore !== null && item.opportunityScore < 25;

        if (isDead && isLowOpp) {
          reasons.push('Dead regime — no meaningful price movement');
          reasons.push(`Low opportunity score (${item.opportunityScore}/100)`);
        } else if (isDead) {
          reasons.push('Dead regime — price is flat with no trend or volatility');
        } else if (isRanging && isLowOpp) {
          reasons.push('Ranging with no clear trend direction');
          reasons.push(`Low opportunity score (${item.opportunityScore}/100)`);
        } else if (item.opportunityScore !== null && item.opportunityScore < 15) {
          reasons.push(`Very low opportunity score (${item.opportunityScore}/100)`);
        }

        if (reasons.length === 0) return null;
        return { ...item, reasons };
      })
      .filter((c): c is WatchlistItemWithData & { reasons: string[] } => c !== null)
      .sort((a, b) => {
        const aDead = a.regime === 'dead' ? 0 : 1;
        const bDead = b.regime === 'dead' ? 0 : 1;
        if (aDead !== bDead) return aDead - bDead;
        return (a.opportunityScore ?? 0) - (b.opportunityScore ?? 0);
      });
  }, [items]);

  const watchlistCount = watchlistMeta?.count ?? items.length;
  const watchlistLimit = watchlistMeta?.limit ?? WATCHLIST_LIMITS.MAX_INSTRUMENTS;
  const isAtCap = watchlistCount >= watchlistLimit;
  const isNearCap = watchlistCount >= WATCHLIST_LIMITS.WARN_THRESHOLD;
  const spotsRemaining = watchlistLimit - watchlistCount;

  const marketOpen = isMarketHours();
  useInterval(fetchWatchlist, marketOpen ? CADENCE.WATCHLIST_PRICE_MS : null);

  // A row is stale when its analysis is over an hour old during market hours,
  // or ANY time the market is closed (weekend data is Friday's close, not live).
  const STALE_AFTER_MS = 60 * 60 * 1000;
  const isStale = useCallback(
    (item: WatchlistItemWithData) =>
      !item.lastAnalysed || !marketOpen || Date.now() - item.lastAnalysed > STALE_AFTER_MS,
    [marketOpen, STALE_AFTER_MS],
  );

  // Fetch watchlist, recommendations, and insider alerts on mount
  useEffect(() => {
    fetchWatchlist();
    fetchRecommendations();
    fetchInsiderAlerts();
  }, [fetchWatchlist, fetchRecommendations, fetchInsiderAlerts]);

  return (
    <Box>
      {/* Page header */}
      <Flex
        className="watchlist-header"
        alignItems="center"
        justifyContent="space-between"
        mb={6}
      >
        <Flex alignItems="baseline" gap={2}>
          <Text as="h1" fontSize="xl" fontWeight={600} color="text.primary">
            Your Watchlist
          </Text>
          {!loading && (
            <Text fontSize="sm" color="text.tertiary">
              ({watchlistCount} of {watchlistLimit})
            </Text>
          )}
        </Flex>
        <Flex className="watchlist-actions" gap={3} alignItems="center">
          {isNearCap && !isAtCap && (
            <Text fontSize="xs" color="status.warning.text" fontWeight={500}>
              {spotsRemaining} spot{spotsRemaining !== 1 ? 's' : ''} remaining
            </Text>
          )}
          {isAtCap && (
            <Text fontSize="xs" color="status.error.text" fontWeight={500}>
              Watchlist full
            </Text>
          )}
          <chakra.input
            type="text"
            placeholder="Search instruments..."
            px={4}
            py={2}
            borderRadius="md"
            border="1px solid"
            borderColor="border.primary"
            bg="bg.primary"
            fontSize={{ base: '16px', md: '14px' }}
            width="240px"
            color="text.primary"
          />
          <Button
            variant="secondary"
            onClick={() => setAddDialogOpen(true)}
            disabled={isAtCap}
            title={isAtCap ? 'Watchlist full. Remove an instrument to make space.' : undefined}
          >
            Add
          </Button>
        </Flex>
      </Flex>

      {/* Account summary bar */}
      {account && (
        <Flex
          className="account-bar"
          alignItems="center"
          gap={6}
          px={4}
          py={3}
          mb={4}
          bg="surface.primary"
          border="1px solid"
          borderColor="border.secondary"
          borderRadius="md"
          flexWrap="wrap"
        >
          <AccountStat label="Portfolio" value={formatGbp(account.total)} />
          <AccountStat label="Cash" value={formatGbp(account.cash)} />
          <AccountStat
            label="Open P&L"
            value={`${account.ppl >= 0 ? '+' : ''}${formatGbp(account.ppl)}`}
            color={account.ppl >= 0 ? 'status.success.text' : 'status.error.text'}
          />
          <AccountStat
            label="Realised"
            value={`${account.result >= 0 ? '+' : ''}${formatGbp(account.result)}`}
            color={account.result >= 0 ? 'status.success.text' : 'status.error.text'}
          />
          <Box ml="auto">
            <Badge variant={autoTrade?.enabled ? 'success' : 'neutral'}>
              {autoTrade?.enabled ? 'Auto-Trade Active' : 'Auto-Trade Off'}
            </Badge>
          </Box>
        </Flex>
      )}

      {/* Watchlist table */}
      <Card>
        {loading ? (
          <SkeletonRows count={8} />
        ) : error ? (
          <Box py={16} px={4} textAlign="center">
            <Text fontSize="lg" fontWeight={600} color="text.primary" mb={2}>
              Unable to load watchlist
            </Text>
            <Text fontSize="sm" color="text.tertiary" mb={4}>
              Something went wrong. Please try again.
            </Text>
            <Button variant="secondary" onClick={fetchWatchlist}>Retry</Button>
          </Box>
        ) : items.length === 0 ? (
          <Box py={16} px={4} textAlign="center">
            <Flex
              width="48px"
              height="48px"
              borderRadius="50%"
              bg="bg.tertiary"
              alignItems="center"
              justifyContent="center"
              mx="auto"
              mb={4}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--chakra-colors-text-tertiary, #667085)" strokeWidth="2" strokeLinecap="round">
                <path d="M12 5V19M5 12H19" />
              </svg>
            </Flex>
            <Text fontSize="lg" fontWeight={600} color="text.primary" mb={2}>
              No instruments yet
            </Text>
            <Text fontSize="sm" color="text.tertiary" mb={4}>
              Add instruments to your watchlist to start monitoring them.
            </Text>
            <Button variant="primary" onClick={() => setAddDialogOpen(true)}>Add instrument</Button>
          </Box>
        ) : (
          <Box>
            {/* Table header */}
            <Grid
              className="watchlist-grid-header"
              templateColumns="80px 1fr 100px 80px 96px 96px 24px"
              gap={3}
              px={4}
              py={3}
              borderBottom="1px solid"
              borderColor="border.secondary"
              fontSize="12px"
              fontWeight={500}
              color="text.tertiary"
              textTransform="uppercase"
              letterSpacing="0.04em"
            >
              <Box as="span">Ticker</Box>
              <Box as="span">Name</Box>
              <Box as="span" textAlign="right">Price</Box>
              <Box as="span" textAlign="right">Change</Box>
              <Box as="span">Signal</Box>
              <Box as="span">Analysed</Box>
              <Box as="span" />
            </Grid>

            {/* Table rows */}
            {items.map((item) => (
              <Grid
                key={item.id}
                className="watchlist-row"
                onClick={() => navigate(`/cockpit/${item.symbol}`)}
                templateColumns="80px 1fr 100px 80px 96px 96px 24px"
                gap={3}
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor="border.secondary"
                width="100%"
                textAlign="left"
                cursor="pointer"
                transition="background-color 150ms ease"
                minHeight="52px"
                alignItems="center"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/cockpit/${item.symbol}`); }}
                _hover={{ bg: 'bg.active' }}
              >
                <Text as="span" fontSize="sm" fontWeight={600} color="text.primary">
                  {item.symbol}
                </Text>
                <Text as="span" fontSize="md" color="text.secondary" className="wl-name">
                  {item.name}
                </Text>
                <PriceDisplay price={item.price} showChange={false} size="sm" style={{ justifyContent: 'flex-end' }} />
                {isStale(item) ? (
                  <Text as="span" fontSize="sm" fontFamily="mono" textAlign="right" color="text.tertiary">
                    &mdash;
                  </Text>
                ) : (
                  <Text
                    as="span"
                    fontSize="sm"
                    fontFamily="mono"
                    textAlign="right"
                    color={item.changePercent >= 0 ? 'status.success.text' : 'status.error.text'}
                  >
                    {item.changePercent >= 0 ? '+' : ''}{item.changePercent.toFixed(2)}%
                  </Text>
                )}
                <Box as="span" className="wl-signal">
                  {isStale(item) ? (
                    // Stale analysis: muted badge with its age, never a live-green signal
                    <Badge variant="neutral">
                      {item.sentiment.charAt(0).toUpperCase() + item.sentiment.slice(1)}
                      {item.lastAnalysed ? ` · ${formatRelativeTime(item.lastAnalysed)}` : ' · no data'}
                    </Badge>
                  ) : (
                    <SentimentBadge sentiment={item.sentiment} />
                  )}
                </Box>
                <Text as="span" fontSize="sm" color="text.tertiary" className="wl-analysed">
                  {item.lastAnalysed ? formatRelativeTime(item.lastAnalysed) : '--'}
                </Text>
                <Box
                  as="button"
                  onClick={(e) => handleRemove(e, item)}
                  title={`Remove ${item.symbol}`}
                  p="4px"
                  borderRadius="sm"
                  color="text.tertiary"
                  opacity={removingId === item.id ? 0.5 : 1}
                  transition="color 150ms ease"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  _hover={{ color: 'status.error.text' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M18 6L6 18M6 6L18 18" />
                  </svg>
                </Box>
              </Grid>
            ))}
          </Box>
        )}
      </Card>

      {/* Review your watchlist section */}
      {!loading && reviewCandidates.length > 0 && (
        <Box mt={6}>
          <Flex alignItems="center" gap={2} mb={1}>
            <Text as="h2" fontSize="lg" fontWeight={600} color="text.primary">
              Review your watchlist
            </Text>
            <Badge variant="warning">{reviewCandidates.length}</Badge>
          </Flex>
          <Text fontSize="sm" color="text.tertiary" mb={4}>
            These instruments may not be worth monitoring — consider replacing them with suggestions below.
          </Text>
          <SimpleGrid minChildWidth="280px" gap={3}>
            {reviewCandidates.map((item) => (
              <Card key={item.id} variant="compact" accentColor="#f79009">
                <Flex alignItems="flex-start" justifyContent="space-between" gap={3}>
                  <Box flex={1} minWidth={0}>
                    <Flex alignItems="center" gap={2} mb={1}>
                      <Text as="span" fontSize="sm" fontWeight={600} color="text.primary">
                        {item.symbol}
                      </Text>
                      {item.regime && <RegimeBadge regime={item.regime} />}
                    </Flex>
                    <Text
                      fontSize="sm"
                      color="text.secondary"
                      mb={2}
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {item.name}
                    </Text>
                    {item.opportunityScore !== null && (
                      <Text fontSize="xs" color="text.tertiary" mb={1}>
                        Opportunity: {item.opportunityScore}/100
                      </Text>
                    )}
                    {item.reasons.map((reason, i) => (
                      <Text key={i} fontSize="xs" color="text.tertiary" lineHeight={1.4}>
                        {reason}
                      </Text>
                    ))}
                  </Box>
                  <Button
                    variant="danger"
                    onClick={(e) => handleRemove(e, item)}
                    disabled={removingId === item.id}
                    flexShrink={0}
                  >
                    Remove
                  </Button>
                </Flex>
              </Card>
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* Insider Alerts Section */}
      {!loading && insiderAlerts.length > 0 && (
        <Box mt={6}>
          <Flex alignItems="center" gap={2} mb={1}>
            <Text as="h2" fontSize="lg" fontWeight={600} color="text.primary">
              Insider Alerts
            </Text>
            <Badge variant="neutral">{insiderAlerts.length}</Badge>
          </Flex>
          <Text fontSize="sm" color="text.tertiary" mb={4}>
            Recent insider purchases detected from SEC Form 4 filings. Public filing data, not a recommendation.
          </Text>
          <SimpleGrid minChildWidth="280px" gap={3}>
            {insiderAlerts.map((alert) => {
              const isWatching = items.some((i) => i.symbol === alert.ticker);
              return (
                <Card key={`${alert.ticker}-${alert.filingDate}`} variant="compact">
                  <Flex alignItems="flex-start" justifyContent="space-between" gap={3}>
                    <Box flex={1} minWidth={0}>
                      <Flex alignItems="center" gap={2} mb={1}>
                        <Text as="span" fontSize="sm" fontWeight={600} color="text.primary">
                          {alert.ticker}
                        </Text>
                        <Badge variant={alert.signalType === 'cluster_buy' ? 'success' : 'neutral'}>
                          {alert.signalType === 'cluster_buy' ? 'Cluster Buy'
                            : alert.signalType === 'ownership_increase' ? 'Ownership Up'
                            : 'Large Purchase'}
                        </Badge>
                      </Flex>
                      <Text
                        fontSize="sm"
                        color="text.secondary"
                        mb={2}
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        {alert.companyName}
                      </Text>
                      <Text fontSize="xs" color="text.tertiary" lineHeight={1.4}>
                        {alert.signalLabel}
                      </Text>
                      {alert.insiderNames.length > 0 && (
                        <Text fontSize="xs" color="text.tertiary" lineHeight={1.4}>
                          {alert.insiderNames.slice(0, 3).join(', ')}
                          {alert.insiderNames.length > 3 && ` +${alert.insiderNames.length - 3} more`}
                        </Text>
                      )}
                      <Text fontSize="xs" color="text.tertiary" mt={1}>
                        Filed {alert.filedDaysAgo === 0 ? 'today' : `${alert.filedDaysAgo}d ago`}
                      </Text>
                    </Box>
                    {isWatching && (
                      <Box flexShrink={0}><Badge variant="success">Watching</Badge></Box>
                    )}
                  </Flex>
                </Card>
              );
            })}
          </SimpleGrid>
        </Box>
      )}

      {/* AI Recommendations Section */}
      {!loading && items.length > 0 && (
        <Box mt={6}>
          <Flex alignItems="center" gap={2} mb={4}>
            <Text as="h2" fontSize="lg" fontWeight={600} color="text.primary">
              Claude's Watchlist
            </Text>
            <Badge variant="neutral">AI</Badge>
          </Flex>

          {recsLoading && recommendations.length === 0 ? (
            <SkeletonRows count={3} />
          ) : recommendations.length === 0 ? (
            <Card>
              <Box textAlign="center" py={6}>
                <Text fontSize="sm" color="text.secondary" mb={1}>
                  All clear — no actionable setups right now
                </Text>
                <Text fontSize="xs" color="text.tertiary">
                  The system is monitoring your {items.length} instruments. You'll see recommendations here when market conditions present a tradeable opportunity.
                </Text>
              </Box>
            </Card>
          ) : (
            <SimpleGrid
              minChildWidth="280px"
              gap={3}
            >
              {recommendations.map((rec) => (
                <Card key={rec.symbol} variant="compact">
                  <Box>
                    <Text
                      as="span"
                      fontSize="sm"
                      fontWeight={600}
                      color="text.primary"
                      display="block"
                      mb={1}
                    >
                      {rec.symbol}
                    </Text>
                    <Text
                      fontSize="sm"
                      color="text.secondary"
                      mb={2}
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {rec.name}
                    </Text>
                    <Text
                      fontSize="xs"
                      color="text.tertiary"
                      lineHeight={1.4}
                    >
                      {rec.reason}
                    </Text>
                  </Box>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </Box>
      )}

      <AddInstrumentDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onAdded={() => { fetchWatchlist(); fetchRecommendations(); }}
        isAtCap={isAtCap}
      />
    </Box>
  );
}

function AccountStat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Flex alignItems="baseline" gap={2}>
      <Text fontSize="xs" color="text.tertiary">{label}</Text>
      <Text fontSize="sm" fontFamily="mono" color={color ?? 'text.primary'} fontWeight={600}>{value}</Text>
    </Flex>
  );
}

function formatGbp(value: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
}

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
